/**
 * The Google Identity Services session.
 *
 * Auth is **GIS directly** — no Cognito, no Auth.js — matching the sibling
 * DragDuro app rather than build-plan §3, which is out of date. The shape:
 * Google signs an ID token in the browser, we hold it, and it rides every
 * request as `Authorization: Bearer <jwt>`. There is no client secret anywhere
 * in this design and no code exchange, because there is no server side to the
 * handshake — the API just verifies Google's signature (§3 as actually built;
 * see apps/api/src/auth/identity.ts).
 *
 * Deliberately framework-free. React talks to it through `AuthProvider`, but
 * the session itself is a module with a subscription, which is what lets the
 * refresh timers live outside the component tree — they must survive a
 * re-render, and a StrictMode double-mount must not mint two of them.
 */

import { decodeToken, isExpired, millisUntilExpiry } from './jwt';
import type { TokenClaims } from './jwt';
import { clearToken, readToken, writeToken } from './tokenStore';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

/**
 * How far ahead of expiry to ask Google for a fresh token.
 *
 * Two minutes is DragDuro's figure and the reasoning carries: long enough that
 * a slow prompt still lands before the old token dies, short enough that a
 * tab left open overnight is not re-prompting hourly for no reason.
 */
const REFRESH_LEAD_MS = 2 * 60 * 1000;

/** Never schedule a timer in the past; fire almost-now instead. */
const MIN_TIMER_MS = 1000;

// --- the GIS global, narrowly typed --------------------------------------
// Only what this module calls. The full surface is large, mostly irrelevant,
// and @types/google.accounts would be a dependency earning its keep for six
// lines.

interface CredentialResponse {
  credential: string;
}

interface GoogleIdApi {
  initialize(config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
    auto_select?: boolean;
    use_fedcm_for_prompt?: boolean;
    cancel_on_tap_outside?: boolean;
    itp_support?: boolean;
  }): void;
  prompt(): void;
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

function gis(): GoogleIdApi | null {
  return window.google?.accounts?.id ?? null;
}

// --- module state ---------------------------------------------------------

export interface Session {
  token: string;
  claims: TokenClaims;
}

let session: Session | null = null;
let ready = false;
const listeners = new Set<(session: Session | null) => void>();

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * The in-flight init promise, cached at module scope.
 *
 * `initAuth` is called from an effect, and StrictMode invokes effects twice in
 * development. Without this, two script tags race and `initialize` runs twice.
 * Caching the promise makes the second call await the first — the standard
 * shape for "do this once per page, no matter who asks".
 */
let initPromise: Promise<void> | null = null;

function notify(): void {
  // One throwing subscriber must not stop the others from hearing about a
  // sign-out — that would leave half the UI showing a session that is gone.
  for (const listener of listeners) {
    try {
      listener(session);
    } catch (err) {
      console.error('Auth listener threw', err);
    }
  }
}

export function subscribe(listener: (session: Session | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function currentSession(): Session | null {
  // Re-check expiry on read rather than trusting the timers. A laptop that
  // slept through the refresh wakes with a stale token and timers that never
  // fired; this is what stops the UI insisting it is still signed in.
  if (session && isExpired(session.claims)) return null;
  return session;
}

export function currentToken(): string | null {
  return currentSession()?.token ?? null;
}

/**
 * The single entry point for every credential.
 *
 * One Tap, the rendered button, and the silent refresh all land here, so
 * persistence, timer re-arming and notification cannot be forgotten on one of
 * the three paths.
 */
function setSession(token: string | null, persist = true): void {
  const claims = token ? decodeToken(token) : null;
  session = token && claims ? { token, claims } : null;

  if (persist) {
    // Only persist a token worth restoring. An already-expired one would just
    // be read back and thrown away on the next load.
    if (session && !isExpired(session.claims)) void writeToken(session.token);
    else void clearToken();
  }

  scheduleRefresh();
  notify();
}

/**
 * Arm the refresh, and the fallback that catches it failing.
 *
 * The second timer is the subtle one, and it is the reason this is not four
 * lines. `prompt()` is not guaranteed to do anything: FedCM can decline it, One
 * Tap has a cooldown after a dismissal, and a user who revoked the grant will
 * never see it resolve. In all of those cases no credential arrives, no
 * callback fires, and the token simply lapses — leaving a UI that still looks
 * signed in and whose every action 401s. So we also arm a timer at the actual
 * expiry: if we get there still holding the old token, the refresh demonstrably
 * failed, and the honest move is to drop to signed-out and show the button.
 *
 * It cancels itself in the happy path — a successful refresh calls `setSession`,
 * which calls this, which clears both timers before re-arming from the new
 * `exp`. So it only ever fires when the refresh genuinely did not happen.
 */
function scheduleRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  if (expiryTimer) clearTimeout(expiryTimer);
  refreshTimer = null;
  expiryTimer = null;
  if (!session) return;

  const untilExpiry = millisUntilExpiry(session.claims);

  refreshTimer = setTimeout(
    () => {
      // `ready` guards the case where a restored session armed this before the
      // GIS script finished loading.
      if (ready) promptSilently();
    },
    Math.max(MIN_TIMER_MS, untilExpiry - REFRESH_LEAD_MS),
  );

  expiryTimer = setTimeout(() => {
    expiryTimer = null;
    if (session && isExpired(session.claims)) setSession(null);
  }, Math.max(MIN_TIMER_MS, untilExpiry));
}

/**
 * Ask Google for a credential without showing anything if it can help it.
 *
 * `prompt()` throws synchronously in some browsers and rejects asynchronously
 * under FedCM in others, and neither is actionable: the user simply is not
 * getting a silent token this time. The expiry fallback above is what notices.
 */
function promptSilently(): void {
  try {
    gis()?.prompt();
  } catch {
    // Falls through to the expiry timer, which drops us to signed-out.
  }
}

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (gis()) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not reach Google sign-in'));
    document.head.appendChild(script);
  });
}

/**
 * Boot the session. Safe to call from an effect, and safe to call twice.
 *
 * Order matters, and it is the reverse of the obvious one: **restore from
 * storage before touching Google.** Reading IndexedDB takes a millisecond;
 * loading and initialising GIS takes a network round trip. Restoring first is
 * what stops a returning user seeing the sign-in screen flash up and vanish on
 * every page load.
 */
export function initAuth(clientId: string): Promise<void> {
  initPromise ??= (async () => {
    const saved = await readToken();
    if (saved) {
      const claims = decodeToken(saved);
      // `persist: false` — this value came *from* storage; writing it back
      // would be a pointless round trip.
      if (claims && !isExpired(claims)) setSession(saved, false);
      else void clearToken();
    }
    // Fires even when nothing was restored: subscribers are waiting to learn
    // they are signed out, which is just as much an answer as a session.
    notify();

    await loadScript();
    const api = gis();
    if (!api) throw new Error('Google sign-in loaded but did not register');

    api.initialize({
      client_id: clientId,
      callback: (response) => setSession(response.credential),
      // Returning users get signed in with no click — and, more importantly,
      // this is what makes the refresh `prompt()` resolve silently.
      auto_select: true,
      // Silent re-auth still works in browsers phasing out third-party
      // cookies. Without it the refresh strategy quietly stops working.
      use_fedcm_for_prompt: true,
      cancel_on_tap_outside: false,
      itp_support: true,
    });
    ready = true;

    // A restored session armed its timers before `ready` was true, so re-arm
    // now that the guard above can pass. Otherwise offer One Tap.
    if (currentSession()) scheduleRefresh();
    else promptSilently();
  })();

  return initPromise;
}

/** Render Google's own button into an element React does not manage. */
export function renderSignInButton(parent: HTMLElement): void {
  gis()?.renderButton(parent, {
    theme: 'filled_black',
    size: 'large',
    shape: 'pill',
    text: 'signin_with',
    logo_alignment: 'left',
  });
}

export function isReady(): boolean {
  return ready;
}

export function signOut(): void {
  try {
    // Without this, `auto_select` signs the user straight back in on the next
    // load — "sign out" would be a no-op with extra steps.
    gis()?.disableAutoSelect();
  } catch {
    // Nothing to undo; clearing our own state below is what matters.
  }
  setSession(null);
}

/**
 * Drop the session because the API rejected the token.
 *
 * The client calls this on a 401. Google's signature can be perfectly valid
 * while the API still refuses us — a disabled account (§9) is exactly that — so
 * the API's verdict, not the token's own `exp`, is the last word.
 */
export function invalidateSession(): void {
  setSession(null);
}

/** Test seam: unwind module state between cases. Not used by the app. */
export function __resetAuthForTests(): void {
  session = null;
  ready = false;
  initPromise = null;
  listeners.clear();
  if (refreshTimer) clearTimeout(refreshTimer);
  if (expiryTimer) clearTimeout(expiryTimer);
  refreshTimer = null;
  expiryTimer = null;
}
